import React, { useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize2, Video, Clock, User, Shield } from 'lucide-react';
import { getVideoDemo, hasVideoDemo } from '../data/videoDemos.js';
import cloudflareStreamService from '../services/cloudflareStreamService.js';
import { useSubscription } from '../contexts/SubscriptionContext.js';

const ExerciseDemonstration = ({ exercise, onClose, videoUrl, videoLoading: propVideoLoading }) => {
  const { subscription } = useSubscription();
  const [videoDemo, setVideoDemo] = useState(null);
  const [cloudflareVideoUrl, setCloudflareVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (exercise && exercise.id) {
      // Reset states
      setVideoError(false);
      setCloudflareVideoUrl(null);
      setVideoDemo(null);
      setIframeLoaded(false);
      setVideoLoading(false);
      setRetryCount(0);
      
      // If videoUrl is provided as prop (like ExerciseModal), use it directly
      if (videoUrl) {
        console.log("Using provided videoUrl:", videoUrl);
        setCloudflareVideoUrl(videoUrl);
        setVideoError(false);
        setVideoLoading(false);
      }
      // Otherwise, generate video URL like before
      else if (exercise.videoId && exercise.platform === 'cloudflare') {
        setVideoLoading(true);
        const userTier = subscription?.tier || 'basic';
        
        // Use the exact same approach as working TAKE5 videos
        cloudflareStreamService.getPlayerUrl(exercise.videoId, userTier)
          .then(url => {
            if (url) {
              console.log("Got video URL:", url);
              setCloudflareVideoUrl(url);
              setVideoError(false);
              setVideoLoading(false);
            } else {
              setVideoError(true);
              setVideoLoading(false);
            }
          })
          .catch(error => {
            console.error('Error loading Cloudflare video:', error);
            setVideoError(true);
            setVideoLoading(false);
          });
      } else {
        // Use traditional video demo
        const demo = getVideoDemo(exercise.id);
        if (demo) {
          setVideoDemo(demo);
        }
      }
    }
  }, [exercise, subscription?.tier, videoUrl]);

  // Simplified video loading - removed complex timeout logic

  // Removed complex retry logic - using simple iframe like ExerciseModal

  const handleVideoTimeUpdate = (e) => {
    setCurrentTime(e.target.currentTime);
  };

  const handleVideoLoadedMetadata = (e) => {
    setDuration(e.target.duration);
  };

  const togglePlay = () => {
    const video = document.getElementById('exercise-video');
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
    }
  };

  const toggleMute = () => {
    const video = document.getElementById('exercise-video');
    if (video) {
      video.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };


  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onClose) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!exercise) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose(e);
        }
      }}
    >
      <div 
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{exercise.name}</h2>
              <p className="text-sm text-gray-600">Exercise Demonstration</p>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClose(e);
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            aria-label="Close demonstration"
            type="button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Video Demo */}
          {videoDemo && (
            <div className="mb-6">
              <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                <video
                  id="exercise-video"
                  className="w-full h-auto"
                  poster={videoDemo.thumbnail}
                  onTimeUpdate={handleVideoTimeUpdate}
                  onLoadedMetadata={handleVideoLoadedMetadata}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                >
                  <source src={videoDemo.videoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                
                {/* Video Controls */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={togglePlay}
                      className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
                    >
                      {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white" />}
                    </button>
                    
                    <button
                      onClick={toggleMute}
                      className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors"
                    >
                      {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
                    </button>
                    
                    <div className="flex-1 text-white text-sm">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-3">
                <h3 className="font-semibold text-gray-900 mb-2">{videoDemo.title}</h3>
                <p className="text-gray-600 text-sm">{videoDemo.description}</p>
              </div>
            </div>
          )}

          {/* Cloudflare Stream Video */}
          {cloudflareVideoUrl && !videoError && (
            <div className="mb-6">
              <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                {videoLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-white">Loading video...</span>
                  </div>
                ) : (
                  <iframe
                    src={cloudflareVideoUrl}
                    className="w-full h-64"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={exercise.name}
                  />
                )}
              </div>
              
              <div className="mt-3">
                <h3 className="font-semibold text-gray-900 mb-2">{exercise.name} Demo</h3>
                <p className="text-gray-600 text-sm">{exercise.description}</p>
              </div>
            </div>
          )}

          {/* Show instructions immediately if video is loading for too long */}
          {cloudflareVideoUrl && videoLoading && (
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm mb-3">
                  <strong>Video is loading...</strong> While you wait, here are the exercise instructions:
                </p>
                {exercise.instructions && (
                  <p className="text-blue-700 text-sm leading-relaxed mb-3">{exercise.instructions}</p>
                )}
                <a
                  href={cloudflareVideoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors font-medium"
                >
                  🎥 Open Video in New Tab
                </a>
              </div>
            </div>
          )}

          {/* Simplified error handling - removed complex retry logic */}
          {!videoDemo && !cloudflareVideoUrl && !videoLoading && (
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-600 text-sm">
                  Video demonstration not available for this exercise.
                </p>
              </div>

              {/* Exercise Diagram if available */}
              {exercise.diagramUrl && (
                <div className="mb-6 bg-gray-50 rounded-lg p-4">
                  <img
                    src={exercise.diagramUrl}
                    alt={`${exercise.name} diagram`}
                    className="w-full max-w-md mx-auto rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="font-semibold text-blue-900 mb-4 text-lg">How to Perform This Exercise</h3>
                
                {/* Instructions */}
                {exercise.instructions && (
                  <div className="mb-4">
                    <p className="text-blue-800 leading-relaxed">{exercise.instructions}</p>
                  </div>
                )}

                {/* Focus Areas */}
                {exercise.focusAreas && exercise.focusAreas.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-blue-900 mb-2 text-sm">Target Areas:</h4>
                    <div className="flex flex-wrap gap-2">
                      {exercise.focusAreas.map((area, index) => (
                        <span key={index} className="bg-blue-200 text-blue-900 px-3 py-1 rounded-full text-sm">
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Exercise Details */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 bg-white rounded-lg p-4">
                  {exercise.sets && (
                    <div>
                      <p className="text-xs text-gray-600 font-semibold">Sets</p>
                      <p className="text-lg font-bold text-blue-900">{exercise.sets}</p>
                    </div>
                  )}
                  {exercise.reps && (
                    <div>
                      <p className="text-xs text-gray-600 font-semibold">Reps</p>
                      <p className="text-lg font-bold text-blue-900">{exercise.reps}</p>
                    </div>
                  )}
                  {exercise.restSeconds && (
                    <div>
                      <p className="text-xs text-gray-600 font-semibold">Rest</p>
                      <p className="text-lg font-bold text-blue-900">{exercise.restSeconds}s</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}




          {/* Safety Information */}
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Shield className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-yellow-800 mb-2">Safety Reminders</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Stop immediately if you feel pain or discomfort</li>
                  <li>• Breathe normally throughout the exercise</li>
                  <li>• Move slowly and with control</li>
                  <li>• Use support (wall, chair) if needed for balance</li>
                  <li>• Consult your healthcare provider if you have concerns</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseDemonstration;